// Sync manager — mirrors the POS inventory sync word-for-word (Android DatabaseViewModel.storeIntoDb
// + InventorySyncViewModel). FULL load: products via the cursor endpoint (product_list_pagination,
// last_id) looped until a short page, promo tables seeded from api/products/updates (since=1969).
// DELTA: a single api/products/updates call every 90s. Watermark = server UTC from api/time, captured
// before the fetch. Taxes come from PHP. Only POS endpoints are used (no update_pagination).

import { db, getMeta, setMeta, clearAll, META_WATERMARK, META_MERCHANT } from "./db";
import { auth, goFullCatalogPage, goInventoryUpdate, goServerTime, phpTaxList } from "./api";
import * as searchIndex from "./searchIndex";

const OLD_DATE = "1969-01-01 00:00:00"; // first-call "get everything" sentinel (POS uses ~1969)
const PAGE_SIZE = 10000; // Android PRODUCT_LIST_PAGE_SIZE
const POLL_MS = 90 * 1000; // Android syncIntervalMillis (90s)

async function applyBundle(result, merchantId, opts = {}) {
  const { skipProducts = false, skipDeleted = false } = opts;
  const r = result || {};
  if (!skipProducts) {
    // `updates` products are a plain array; `update_pagination` wraps them in `.items` — handle both.
    const products = (r.products && (r.products.items || (Array.isArray(r.products) ? r.products : []))) || [];
    const withMerchant = products.filter((p) => p && p.id != null).map((p) => ({ ...p, merchant: merchantId }));
    if (withMerchant.length) {
      await db.products.bulkPut(withMerchant);
      searchIndex.upsertProducts(withMerchant); // keep the in-memory search index fresh
    }
  }
  if (r.collections) await db.categories.bulkPut(r.collections.filter((x) => x && x.id != null));
  if (r.matches) await db.matches.bulkPut(r.matches.filter((x) => x && x.id != null));
  if (r.bogos) await db.bogos.bulkPut(r.bogos.filter((x) => x && x.id != null));
  if (r.coupons) await db.coupons.bulkPut(r.coupons.filter((x) => x && x.id != null));
  if (r.loyalty_program) await setMeta("loyalty_program", r.loyalty_program);
  // On a full sync (since=old) deleted_items is the entire deletion history (thousands) and pointless
  // after clearAll — skip it. Deltas apply it normally.
  if (!skipDeleted) await applyDeleted(r.deleted_items);
}

async function applyDeleted(items) {
  if (!items || !items.length) return;
  const byType = {};
  for (const d of items) {
    const t = d.item_type;
    (byType[t] = byType[t] || []).push(String(d.item_id));
  }
  if (byType.product) {
    await db.products.bulkDelete(byType.product);
    searchIndex.removeProducts(byType.product);
  }
  if (byType.collection) await db.categories.bulkDelete(byType.collection);
  if (byType.mix_match_pricing) await db.matches.bulkDelete(byType.mix_match_pricing);
  if (byType.coupon) await db.coupons.bulkDelete(byType.coupon);
  if (byType.bogo_tbl) await db.bogos.bulkDelete(byType.bogo_tbl);
}

// Load the full product catalog via the cursor endpoint — word-for-word with Android's storeIntoDb
// product loop: start last_id=0, insert each page, advance the cursor to the last product's id, and
// stop when a page is short (< PAGE_SIZE) or that id is missing/non-numeric (so it can never loop
// forever). Uses the raw response length for the stop test, exactly like Android's `products.size`.
async function pullAllProducts(merchantId, onProgress) {
  let lastId = 0;
  let pages = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const resp = await goFullCatalogPage(lastId, PAGE_SIZE);
    const rows = Array.isArray(resp?.result) ? resp.result : [];
    const pageCount = rows.length;
    const valid = rows.filter((p) => p && p.id != null).map((p) => ({ ...p, merchant: merchantId }));
    if (valid.length) {
      await db.products.bulkPut(valid);
      searchIndex.upsertProducts(valid);
    }
    pages += 1;
    if (onProgress) onProgress(Math.min(98, 10 + pages * 12)); // coarse forward progress (size unknown)
    if (pageCount < PAGE_SIZE) break; // last page (Android: products==null || size < limit)
    const nextId = parseInt(rows[rows.length - 1].id, 10);
    if (!Number.isFinite(nextId)) break; // Android: last product id null/non-numeric → stop
    lastId = nextId;
  }
}

async function fetchTaxes() {
  try {
    const res = await phpTaxList();
    const list = (res?.result || []).filter((t) => t && t.id != null);
    if (list.length) {
      await db.taxes.clear();
      await db.taxes.bulkPut(list);
    }
  } catch (e) {
    // taxes fetch failure is non-fatal for sync; register can still render (pricing tax may be off)
    // eslint-disable-next-line no-console
    console.warn("[web-register] tax_list fetch failed", e);
  }
}

export async function fullSync(onProgress) {
  const { merchantId } = auth();
  const serverTime = await goServerTime(); // watermark captured BEFORE the fetch (Android: getServerTime)
  await clearAll();
  searchIndex.clearIndex();
  if (onProgress) onProgress(2);
  // Promo/collection tables first, seeded from POS's own api/products/updates (since=1969 → the whole
  // set). Same endpoint + Go shape as the 90s delta, so full-load and delta rows never diverge. Its
  // product array is ignored here (skipProducts) — products load via the cursor loop below.
  const bundle = await goInventoryUpdate(OLD_DATE);
  await applyBundle(bundle?.result, merchantId, { skipProducts: true, skipDeleted: true });
  if (onProgress) onProgress(8);
  await pullAllProducts(merchantId, onProgress); // cursor-paginated product list (Android last_id loop)
  await fetchTaxes();
  await setMeta(META_MERCHANT, merchantId);
  await setMeta(META_WATERMARK, serverTime || OLD_DATE);
  if (onProgress) onProgress(100);
}

// SAME-TAB guards so the background prewarm, the register's on-open sync, and the 90s poll don't
// overlap within one tab (which would race the shared `since` watermark).
let ensureInFlight = null; // shared promise while a full/open sync runs (this tab)
let deltaInFlight = false; // true while a poll delta runs (this tab)

// CROSS-TAB lock: the register opens in a NEW TAB, so the per-tab guards above don't cover "prewarm
// building in tab A while the register opens in tab B" — both share ONE origin IndexedDB. The Web
// Locks API serializes the actual sync (network + IDB writes) to one tab at a time. The waiting tab
// then finds the DB already built and does a quick delta instead of re-downloading everything.
async function withSyncLock(fn) {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  if (nav && nav.locks && typeof nav.locks.request === "function") {
    return nav.locks.request("qv_web_register_sync", fn);
  }
  return fn(); // older browsers without Web Locks fall back to per-tab guards only
}

async function _pullDelta() {
  const { merchantId } = auth();
  if (!merchantId) return; // not authed / logged out — never touch the DB
  const storedMerchant = await getMeta(META_MERCHANT);
  // Store switched under an open register: the DB belongs to a different merchant. Merging deltas
  // across merchants would corrupt the catalog, so rebuild for the current store instead. (The UI
  // reload from useStoreSwitchReload normally fires first; this makes the data layer self-healing.)
  if (storedMerchant !== merchantId) {
    await fullSync();
    await searchIndex.buildFromDb();
    return;
  }
  const last = (await getMeta(META_WATERMARK)) || OLD_DATE;
  const serverTime = await goServerTime(); // watermark captured BEFORE the fetch (Android getServerTime)
  const res = await goInventoryUpdate(last); // single api/products/updates — plain-array bundle, no page loop
  await applyBundle(res?.result, merchantId); // upsert products + promos, apply deleted_items (Android updateTables)
  await setMeta(META_WATERMARK, serverTime || last);
}

// Public 90s-poll entry — SKIPS if a build/open sync or another delta is already running in this tab;
// also holds the cross-tab lock so it can't overlap a full build in another tab.
export async function deltaSync() {
  if (ensureInFlight || deltaInFlight) return;
  deltaInFlight = true;
  try {
    await withSyncLock(() => _pullDelta());
  } finally {
    deltaInFlight = false;
  }
}

// Idempotent open/prewarm sync. Same-tab concurrent callers (prewarm + register open) share ONE run;
// across tabs the Web Lock ensures only one tab downloads the catalog and the other just deltas.
/**
 * Android home ⋮ → Sync (bdaba0aa71, QV-7471): a cashier-started FULL re-sync. Refuses while another
 * sync runs in this tab ("busy"); runs under the cross-tab lock and rebuilds the search index.
 */
export async function manualFullSync(onProgress) {
  if (ensureInFlight || deltaInFlight) return { status: "busy" };
  ensureInFlight = (async () => {
    await withSyncLock(() => fullSync(onProgress));
    await searchIndex.buildFromDb();
    return { status: "ok" };
  })();
  try {
    return await ensureInFlight;
  } finally {
    ensureInFlight = null;
  }
}

export async function ensureSynced(onProgress) {
  if (ensureInFlight) return ensureInFlight;
  ensureInFlight = (async () => {
    // The merchant/count check runs INSIDE the lock, so a tab that waited for another tab's full build
    // sees the freshly-populated DB (count > 0) and takes the quick delta path — never a rebuild.
    const full = await withSyncLock(async () => {
      const { merchantId } = auth();
      const storedMerchant = await getMeta(META_MERCHANT);
      const count = await db.products.count();
      if (storedMerchant !== merchantId || count === 0) {
        await fullSync(onProgress);
        return true;
      }
      try {
        await _pullDelta();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[web-register] delta catch-up failed on open", e);
      }
      return false;
    });
    // Build the in-memory search index from the (now-synced) IndexedDB catalog — per-tab (in-memory
    // isn't shared across tabs), so both tabs build their own after the locked sync.
    await searchIndex.buildFromDb();
    return { full };
  })();
  try {
    return await ensureInFlight;
  } finally {
    ensureInFlight = null;
  }
}

// 90s foreground poll (pauses when tab hidden / offline). Returns a stop function. `onTick` fires
// after each delta attempt so the UI can re-read the (possibly updated) catalog from IndexedDB.
export function startPolling(onError, onTick) {
  const tick = () => {
    if (typeof document !== "undefined" && document.hidden) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    deltaSync()
      .then(() => onTick && onTick())
      .catch((e) => onError && onError(e));
  };
  const id = setInterval(tick, POLL_MS);
  return () => clearInterval(id);
}
